import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useAuthedBlobUrl } from '@/shared/api/authedBlob';

import { FileGlyph } from './FileGlyph';

interface DocThumbProps {
  /** Путь к мини-картинке через наш авторизованный прокси (relative, кука).
   *  Если undefined — рендерим обычную иконку. */
  thumbUrl?: string;
  className?: string;
  accent?: boolean;
  /** Чем рисовать, пока (или если) картинки нет. По умолчанию — FileGlyph. */
  fallback?: ReactNode;
}

/** Иконка файла с ленивой мини-превьюшкой для картинок. Байты тянутся только
 *  когда строка попала во вьюпорт (IntersectionObserver), картинка маленькая
 *  (thumbnail с бэка). Пока не загрузилось / ошибка / не картинка — иконка. */
export function DocThumb({
  thumbUrl,
  className,
  accent = false,
  fallback,
}: DocThumbProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!thumbUrl || visible) {
      return;
    }
    const node = ref.current;
    if (!node) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [thumbUrl, visible]);

  const { blobUrl, error } = useAuthedBlobUrl(thumbUrl, visible);
  const showImg = Boolean(thumbUrl) && Boolean(blobUrl) && !error;

  return (
    <span ref={ref} className={className}>
      {showImg ? (
        <img
          src={blobUrl as string}
          alt=""
          loading="lazy"
          className="h-full w-full rounded-[4px] object-cover"
        />
      ) : (
        (fallback ?? <FileGlyph className="h-full w-full" accent={accent} />)
      )}
    </span>
  );
}
